import { useTranslation } from 'react-i18next';
import { IQuestion } from '../../../../types/ApiTypes';
import { DropdownOption } from '../../../../types/Dropdown.types';
import { Langs, QuestionType, QuestionTypes } from '../../../../types/GlobalTypes';
import { getCoordinatesStringFromLatLng } from '../../../../utils/general/getCoordinatesStringFromLatLng/getCoordinatesStringFromLatLng';
import { answersDefault, multilangStringDefault } from '../static/QuestionFormDefaults';

const findQuestionTypeDropdownOption = ({ type, options }: { type: QuestionType; options: DropdownOption[] }) =>
  options.find(option => option.id === type);

const useDefaultQuestionValues = () => {
  const { t } = useTranslation();

  const getDefaultQuestionValues = ({
    question,
    questionTypeOptions,
  }: {
    question?: IQuestion;
    questionTypeOptions: DropdownOption[];
  }) => {
    const isAnimator = question?.questionType.code === QuestionTypes.ANIMATOR;
    const isMedia =
      question?.questionType.code === QuestionTypes.VIDEO || question?.questionType.code === QuestionTypes.IMAGE;

    const obj = {
      tags: question?.tags.map(tag => ({
        id: tag.id.toString(),
        value: tag.tagName,
      })),
      name: question?.name ?? multilangStringDefault,
      questionType: !!question
        ? findQuestionTypeDropdownOption({ type: question.questionType.code, options: questionTypeOptions })
        : questionTypeOptions[0],
      content: question?.content ?? multilangStringDefault,
      correctAnswers: question?.correctAnswers ?? answersDefault,
      incorrectAnswers: question?.incorrectAnswers ?? answersDefault,
      points: question?.points ?? undefined,
      // pointsOptions is the animator's selectable values; for video/image it is a
      // sentinel meaning "points editable during evaluation" (see prepareQuestionRequestData).
      multiplePointsOptions:
        isAnimator && !!question?.pointsOptions
          ? question.pointsOptions.map(option => ({
              id: option.toString(),
              value: option.toString(),
            }))
          : [],
      pointsEditableDuringEvaluation: isMedia ? !!question?.pointsOptions : false,
      city: question?.city ?? '',
      coordinates: getCoordinatesStringFromLatLng(question?.coordinates),
      // GMT-791: cover every supported language, not only PL/EN/UA
      correctAnswerText:
        question?.correctAnswerText ??
        Object.values(Langs).map(lang => ({
          lang,
          value: t('questionForm.answerMessages.defaultCorrectAnswerText'),
        })),
      incorrectAnswerText:
        question?.incorrectAnswerText ??
        Object.values(Langs).map(lang => ({
          lang,
          value: t('questionForm.answerMessages.defaultIncorrectAnswerText'),
        })),
    };

    return obj;
  };

  return { getDefaultQuestionValues };
};

export default useDefaultQuestionValues;
